/**
 * サーバが発行した署名付き URL でアップロードバケットへファイルを上げる。
 *
 * ブラウザは AWS の資格情報を持たない。キー・バケット・タグ・ACL はサーバが決め、
 * ブラウザは発行済みのキー (key / expires_at / upload_token) を署名 API に渡すだけ。
 *
 * 振る舞いは以前の @aws-sdk/lib-storage の Upload に合わせている。
 * - ファイルが partSize 以下なら 1 回の PUT、超えるならマルチパート (queueSize 本ずつ並列)
 * - 進捗は PUT の完了時と、各パートの完了時に { loaded, total } で知らせる
 * - パートが 1 つでも失敗したら、走っている残りを待ってからマルチパートを中止し、最初のエラーを投げる
 * - S3 への各リクエストは最大 3 回まで試す (S3Client の既定 maxAttempts と同じ)。
 *   試すたびに署名 URL を取り直すため、URL の期限切れもここで回復する
 */

const MAX_ATTEMPTS = 3;
const MAX_PARTS = 10000;

const readCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const postJson = async (url, payload, csrfToken) => {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data && data.message) || `HTTP ${res.status}`);
  }
  return data;
};

/**
 * sign() で署名 URL を取り、body を PUT する。失敗したら URL を取り直して試し直す。
 * 署名 API 自体の失敗 (認可・検証エラー) は試し直さずにそのまま投げる。
 */
const putWithRetry = async (sign, body, extraHeaders) => {
  let lastError = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    const signed = await sign();
    let res;
    try {
      res = await fetch(signed.url, {
        method: 'PUT',
        headers: { ...(signed.headers || {}), ...extraHeaders },
        body,
      });
    } catch (err) {
      lastError = err;
      continue;
    }
    if (res.ok) {
      return res;
    }
    lastError = new Error(`S3 PUT failed (HTTP ${res.status})`);
  }
  throw lastError;
};

/**
 * @param {object} options
 * @param {File|Blob} options.file
 * @param {{put:string, create:string, sign:string, complete:string, abort:string}} options.endpoints 署名 API
 * @param {{key:string, expires_at:number, upload_token:string}} options.grant 発行 API が返したキーと署名
 * @param {number} options.partSize
 * @param {number} options.queueSize
 * @param {string} [options.contentType] オブジェクトの Content-Type (空なら付けない)
 * @param {string|null} [options.contentDisposition] 'attachment' のときだけ付ける
 * @param {(evt:{loaded:number,total:number}) => void} [options.onProgress]
 * @param {string} [options.csrfToken] 省略時は meta[name=csrf-token]
 * @returns {Promise<{key:string}>}
 */
export async function uploadWithPresignedUrls({
  file,
  endpoints,
  grant,
  partSize,
  queueSize,
  contentType = '',
  contentDisposition = null,
  onProgress = null,
  csrfToken = null,
}) {
  if (!endpoints || !grant || !grant.key) {
    throw new Error('upload grant is missing');
  }
  const csrf = csrfToken || readCsrfToken();
  const total = file.size;
  const grantPayload = {
    key: grant.key,
    expires_at: grant.expires_at,
    upload_token: grant.upload_token,
  };
  const notify = (loaded) => {
    if (onProgress) onProgress({ loaded, total });
  };

  if (total <= partSize) {
    const headers = contentType ? { 'Content-Type': contentType } : {};
    await putWithRetry(
      () => postJson(endpoints.put, { ...grantPayload, content_disposition: contentDisposition }, csrf),
      file,
      headers
    );
    notify(total);
    return { key: grant.key };
  }

  const totalParts = Math.ceil(total / partSize);
  if (totalParts > MAX_PARTS) {
    throw new Error(`Exceeded ${MAX_PARTS} parts in multipart upload. Key: ${grant.key}.`);
  }

  const created = await postJson(endpoints.create, {
    ...grantPayload,
    content_type: contentType || null,
    content_disposition: contentDisposition,
  }, csrf);
  const multipartPayload = {
    key: grant.key,
    upload_id: created.upload_id,
    multipart_expires_at: created.multipart_expires_at,
    multipart_token: created.multipart_token,
  };

  const parts = [];
  const failures = [];
  let nextPart = 1;
  let loaded = 0;
  const worker = async () => {
    while (failures.length === 0 && nextPart <= totalParts) {
      const partNumber = nextPart;
      nextPart += 1;
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, total);
      const chunk = file.slice(start, end);
      try {
        const res = await putWithRetry(
          () => postJson(endpoints.sign, { ...multipartPayload, part_number: partNumber }, csrf),
          chunk,
          {}
        );
        const etag = res.headers.get('ETag');
        if (!etag) {
          throw new Error(`Part ${partNumber} is missing ETag in UploadPart response. Missing Bucket CORS configuration for ETag header?`);
        }
        parts.push({ PartNumber: partNumber, ETag: etag });
        loaded += end - start;
        notify(loaded);
      } catch (err) {
        failures.push(err);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(queueSize, totalParts) }, () => worker()));

  if (failures.length > 0) {
    await postJson(endpoints.abort, multipartPayload, csrf).catch(() => {});
    throw failures[0];
  }

  parts.sort((a, b) => a.PartNumber - b.PartNumber);
  await postJson(endpoints.complete, { ...multipartPayload, parts }, csrf);
  return { key: grant.key };
}
